import { useContext, useEffect, useState } from 'react';
import { MdOutlineLocalLaundryService } from 'react-icons/md';
import { FormattedMessage, useIntl } from 'react-intl';

import { TerminalContext, TerminalContextInterface } from '../../TerminalInit';
import { Slot, useSlots } from '../../api/slots';
import { useSpotLayoutItems } from '../../api/spotLayoutItems';
import { useSpotLayout } from '../../api/spotLayouts';
import { SpotType, useMutateSpotLaundryPickup, useSpot } from '../../api/spots';
import ActionCard from '../../components/domain/ActionCard';
import { AllowedActionViewProps } from '../../components/domain/AllowedAction';
import useSpotLayoutItemsForSlots from '../../hooks/useSpotLayoutItemForSlot';
import { SlotOpenState, SlotOpenView } from '../../views/SlotOpenView';
import BackAndHomeNavigationButtons from '../../views/common/BackAndHomeNavigationButtons';
import BaseView from '../../views/common/BaseView';
import InfoView from '../../views/common/InfoView';
import LoadingView from '../../views/common/LoadingView';
import SlotOpenSpotLayout from '../../views/spotlayout/slotOpenSpotLayouts/SlotOpenSpotLayout';

type NikeLaundryPickupActionProps = AllowedActionViewProps;

const NikeLaundryPickupView = (props: NikeLaundryPickupActionProps) => {
    const intl = useIntl();
    const { terminal } = useContext<TerminalContextInterface>(TerminalContext);
    const { data: spotLayout } = useSpotLayout(terminal?.spot_layout_url);
    const { data: spotLayoutItems } = useSpotLayoutItems({ spotLayout: spotLayout }, { enabled: !!spotLayout });
    const { data: spot } = useSpot(spotLayout?.spot_url);
    const { data: laundrySlots } = useSlots({ spot: spot?.id, type: 'pickup_laundry' }, { enabled: !!spot });
    const [slotsConfirmed, changeSlotsConfirmed] = useState<boolean>(false);
    const [error, changeError] = useState<string | null>(null);
    const [slots, changeSlots] = useState<Slot[] | null>(null);
    const slotItems = useSpotLayoutItemsForSlots(laundrySlots ?? []);

    const backHomeNav = (
        <BackAndHomeNavigationButtons
            onHome={props.onHome}
            onBack={props.onBack}
        />
    );

    const mutateSpotLaundryPickup = useMutateSpotLaundryPickup({
        onSuccess: (result) => {
            changeSlots(result);
        },
        onError: (error) => {
            changeError(
                intl.formatMessage({
                    id: 'workflow.AuthorisedContactWorkflow.NikeLaundryPickupAction.noTransactionFound',
                    description: 'Error message when no laundry transaction is found for pickup',
                    defaultMessage: 'No laundry transaction found for pickup.',
                })
            );
        },
    });

    useEffect(() => {
        if (spot && slotsConfirmed) {
            mutateSpotLaundryPickup.mutate({
                spot_id: spot.id,
            });
        }
    }, [spot, slotsConfirmed]);

    if (error)
        return (
            <InfoView
                navbarItems={backHomeNav}
                onInactivity={props.onInactivity}
                title={error}
            />
        );

    if (!laundrySlots || slotItems === undefined) {
        return (
            <LoadingView
                onInactivity={props.onInactivity}
                onHome={props.onHome}
                onBack={props.onBack}
            />
        );
    }

    if (!slotsConfirmed) {
        return (
            <BaseView
                onInactivity={props.onInactivity}
                navbarItems={backHomeNav}>
                <div className='text-center'>
                    <button
                        className='primary-button btn-lg'
                        onClick={() => changeSlotsConfirmed(true)}>
                        <FormattedMessage
                            id='views.SlotConfirmationView.confirmButton'
                            description='The button label to confirm using the proposed Slot.'
                            defaultMessage='Confirm and open SLOT'
                        />
                    </button>
                </div>
                <SlotOpenSpotLayout
                    type={SpotType.STANDARD_DISTRISPOT}
                    highlightedItems={slotItems}
                />
            </BaseView>
        );
    }

    if (!slots) {
        return (
            <LoadingView
                onInactivity={props.onInactivity}
                onHome={props.onHome}
                onBack={props.onBack}
            />
        );
    }

    return (
        <SlotOpenView
            spotType={spot ? spot.type : SpotType.STANDARD_DISTRISPOT}
            slotInfos={slots.map((s) => {
                return { slot: s, state: SlotOpenState.SUCCESS };
            })}
            onInactivity={props.onInactivity}
            onHome={props.onHome}
        />
    );
};

function NikeLaundryPickupAction(props: NikeLaundryPickupActionProps) {
    const intl = useIntl();
    if (props.contact === undefined) return undefined;
    if (!props.contact.laundry) return undefined;
    if (props.contact.additional_data && (props.contact.additional_data as any).role && (props.contact.additional_data as any).role !== 'laundry_service')
        return undefined;

    const nikeLaundryPickupAction: ActionCard = {
        actionText: intl.formatMessage({
            id: 'workflow.AuthorisedContactWorkflow.NikeLaundryPickupAction',
            description: 'This is the Laundry Pickup action for AuthorisedContactWorkflow',
            defaultMessage: 'Laundry PickUp',
        }),
        highlight: true,
        icon: (
            <MdOutlineLocalLaundryService
                className='dashboard-action-icon'
                size={264}
            />
        ),
        view: <NikeLaundryPickupView {...props} />,
    };

    return nikeLaundryPickupAction;
}

export default NikeLaundryPickupAction;
